import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setTokenExpireTime,
  setShowRefreshWarning,
  setRefreshTimeoutId,
  setWarningTimeoutId,
  clearTimeouts,
  setSessionExpired,
} from '../features/authentication/authSlice';
import { cacheGet } from '../util/cache';
import { RefreshTokenCreateService } from '../services/auth.service';
import axios from 'axios';
import configs from '../config/app';
import useGeneratePillarsToken from './useGeneratePillarsToken';
import { flagEnabled } from '../util/flags';
import { clearCacheItems } from '../util/account';
import useFlagsHook from './useFlags.hook';
/**
 * Custom hook to manage token refresh and session expiration.
 * It sets up timers to warn the user before token expiration,
 * refreshes the token, and handles logout on expiration or refresh failure.
 */
const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const { isFlagEnabled } = useFlagsHook();
  const { tokenExpireTime, showRefreshWarning } = useSelector((state) => state.auth);
  const { refresh } = useGeneratePillarsToken();

  // Function to perform logout
  const performLogout = useCallback(() => {
    if (isFlagEnabled('debugging')) {
      console.log('Performing logout...');
    }
    // First, clear all timeouts to prevent further state changes
    dispatch(clearTimeouts());

    // Clear all storage items completely
    sessionStorage.clear();
    clearCacheItems();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("showWalletBalance_")) {
        localStorage.removeItem(key);
        // Decrement index since localStorage key count shrinks after removal
        i--;
      }
    }
    // Ensure session expired state is set
    dispatch(setSessionExpired(true));

    // Use setTimeout to allow React to finish current render cycle before redirect
    setTimeout(() => {
      // Use replace instead of href to prevent back button issues
      window.location.replace(configs.defaultHomePage);

      // Fallback: If replace doesn't work for some reason, force a reload after a short delay
      setTimeout(() => {
        if (
          window.location.pathname !== configs.defaultHomePage &&
          window.location.pathname !== '/'
        ) {
          if (isFlagEnabled('debugging')) {
            console.log('Fallback: Force reloading page...');
          }
          window.location.reload();
        }
      }, 1000);
    }, 100); // Increased timeout slightly to ensure state changes complete
  }, [dispatch]);

  // Function to setup token refresh timing
  const setupTokenRefresh = useCallback(() => {
    const apiToken = cacheGet('apiToken', true, null);

    if (!apiToken || !apiToken.jwtToken) {
      if (isFlagEnabled('debugging')) {
        console.log('No API token found, skipping token refresh setup');
      }
      return;
    }

    // Clear existing timeouts
    dispatch(clearTimeouts());

    // Calculate expiration time with max 30 minutes
    let jwtExpireTime;
    let expireTime;

    try {
      // Try to decode JWT to get expiration time
      const tokenPayload = JSON.parse(atob(apiToken.jwtToken.split('.')[1]));
      if (tokenPayload.exp) {
        jwtExpireTime = new Date(tokenPayload.exp * 1000);
        if (isFlagEnabled('debugging')) {
          console.log(
            'Token expiration found in JWT:',
            jwtExpireTime.toISOString(),
            tokenPayload.exp
          );
        }
      }
    } catch (error) {
      console.warn('Could not decode JWT token:', error);
    }

    // Set expiration to max 30 minutes from now, or JWT expiration if shorter
    const maxExpireTime = new Date(Date.now() + 30 * 60 * 1000); // 30 minutes from now

    if (jwtExpireTime && jwtExpireTime > new Date()) {
      // Use the shorter of JWT expiration or 30 minutes
      expireTime = jwtExpireTime < maxExpireTime ? jwtExpireTime : maxExpireTime;
      if (isFlagEnabled('debugging')) {
        console.log(
          'Using expiration time (min of JWT and 30min):',
          expireTime.toISOString()
        );
      }
    } else {
      // Fallback to 30 minutes from now if we can't get exp time or token is expired
      expireTime = maxExpireTime;
      if (isFlagEnabled('debugging')) {
        console.log('Using fallback expiration time (30min):', expireTime.toISOString());
      }
    }

    dispatch(setTokenExpireTime(expireTime.toISOString()));

    const currentTime = new Date();
    const timeUntilExpire = expireTime - currentTime;
    if (isFlagEnabled('debugging')) {
      console.log({
        currentTime: currentTime.toISOString(),
        expireTime: expireTime.toISOString(),
        timeUntilExpireMs: timeUntilExpire,
        timeUntilExpireSec: Math.round(timeUntilExpire / 1000),
        timeUntilExpireMin: Math.round(timeUntilExpire / 60000),
      });
    }
    // Show warning at 5 minutes before expiration
    const warningTime = timeUntilExpire - 5 * 60 * 1000; // 5 minutes before expiration

    if (warningTime > 0) {
      const warningTimeoutId = setTimeout(() => {
        if (isFlagEnabled('debugging')) {
          console.log('Token refresh warning triggered - 5 minutes until expiration');
        }
        dispatch(setShowRefreshWarning(true));
      }, warningTime);
      if (isFlagEnabled('debugging')) {
        console.log('Warning will show in:', Math.round(warningTime / 1000), 'seconds');
      }
      dispatch(setWarningTimeoutId(warningTimeoutId));
    } else if (timeUntilExpire > 0) {
      // If less than 5 minutes remaining, show warning immediately
      if (isFlagEnabled('debugging')) {
        console.log('Less than 5 minutes remaining - showing warning immediately');
      }
      dispatch(setShowRefreshWarning(true));
    }

    // Set session expired at expiration time
    if (timeUntilExpire > 0) {
      const refreshTimeoutId = setTimeout(() => {
        // Double-check if token is still valid before logout (in case it was refreshed in another tab)
        const currentToken = cacheGet('apiToken', true, null);
        if (currentToken?.jwtToken) {
          try {
            const tokenPayload = JSON.parse(atob(currentToken.jwtToken.split('.')[1]));
            if (tokenPayload.exp) {
              const tokenExpireTime = new Date(tokenPayload.exp * 1000);
              const now = new Date();

              if (tokenExpireTime > now) {
                if (isFlagEnabled('debugging')) {
                  console.log(
                    'Token was refreshed in another tab, resetting timers instead of logout'
                  );
                }
                // Token is still valid, reset timers instead of logout
                setupTokenRefresh();
                return;
              }
            }
          } catch (error) {
            console.warn('Could not verify token expiration before logout:', error);
          }
        }

        if (isFlagEnabled('debugging')) {
          console.log('Token has expired - performing logout');
        }
        // Hide warning first to prevent UI flash
        dispatch(setShowRefreshWarning(false));
        // Set session expired first
        dispatch(setSessionExpired(true));
        // Perform logout when token expires
        performLogout();
      }, timeUntilExpire);
      if (isFlagEnabled('debugging')) {
        console.log(
          'Token will expire and logout in:',
          Math.round(timeUntilExpire / 1000),
          'seconds'
        );
      }
      dispatch(setRefreshTimeoutId(refreshTimeoutId));
    } else {
      if (isFlagEnabled('debugging')) {
        console.log('Token already expired - performing immediate logout');
      }
      // Set session expired first
      dispatch(setSessionExpired(true));
      performLogout();
    }
  }, [dispatch, performLogout]);

  // Function to refresh the token
  const refreshToken = useCallback(async () => {
    try {
      await RefreshTokenCreateService(axios);
      // Hide warning and reset timers after successful refresh
      dispatch(setShowRefreshWarning(false));
      setupTokenRefresh(); // Setup new timers with refreshed token
      refresh(); // Refresh Pillars token as well

      // Notify other tabs about token refresh
      localStorage.setItem(
        'tokenRefreshed',
        JSON.stringify({
          timestamp: Date.now(),
        })
      );

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      dispatch(setShowRefreshWarning(false));
      // Set session expired first when token refresh fails
      dispatch(setSessionExpired(true));
      // Perform logout when token refresh fails
      performLogout();
      return false;
    }
  }, [dispatch, setupTokenRefresh, performLogout, refresh]);

  // Function to dismiss warning and logout
  const dismissAndLogout = useCallback(() => {
    dispatch(setShowRefreshWarning(false));
    // Set session expired first
    dispatch(setSessionExpired(true));
    // Perform logout
    performLogout();
  }, [dispatch, performLogout]);

  // Setup token refresh on mount and when token changes
  useEffect(() => {
    const apiToken = cacheGet('apiToken', true, null);
    if (apiToken?.jwtToken) {
      setupTokenRefresh();
    }

    // Listen for token refresh events from other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'tokenRefreshed' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          if (data.timestamp > Date.now() - 5000) {
            // Within last 5 seconds
            if (isFlagEnabled('debugging')) {
              console.log('Token refreshed in another tab, resetting timers');
            }
            setupTokenRefresh(); // Reset timers with new token
            dispatch(setShowRefreshWarning(false));
          }
        } catch (error) {
          console.warn('Error parsing token refresh event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Add periodic check as a safety net (check every 30 seconds)
    const intervalId = setInterval(() => {
      const currentApiToken = cacheGet('apiToken', true, null);
      if (!currentApiToken || !currentApiToken.jwtToken) {
        if (isFlagEnabled('debugging')) {
          console.log('Periodic check: No token found, clearing interval');
        }
        clearInterval(intervalId);
        return;
      }

      try {
        // Try to decode JWT to get expiration time
        const tokenPayload = JSON.parse(atob(currentApiToken.jwtToken.split('.')[1]));
        if (tokenPayload.exp) {
          const expireTime = new Date(tokenPayload.exp * 1000);
          const currentTime = new Date();

          if (expireTime <= currentTime) {
            if (isFlagEnabled('debugging')) {
              console.log('Periodic check: Token has expired, triggering logout');
            }
            clearInterval(intervalId);
            dispatch(setSessionExpired(true));
            performLogout();
          }
        }
      } catch (error) {
        if (isFlagEnabled('debugging')) {
          console.warn('Periodic check: Could not decode JWT token:', error);
        }
      }
    }, 30000); // Check every 30 seconds

    // Cleanup timeouts and interval on unmount
    return () => {
      dispatch(clearTimeouts());
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setupTokenRefresh, dispatch, performLogout]);

  return {
    showRefreshWarning,
    tokenExpireTime,
    refreshToken,
    dismissAndLogout,
    setupTokenRefresh,
  };
};

export default useTokenRefresh;
